import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Vendor CSS first so app classes win at equal specificity (niraxobid.md 23.14).
import 'primeicons/primeicons.css';
import './assets/icons/customIcons.css';
import 'primeflex/primeflex.css';
import './index.css';

import App from './App';
import { store } from '@/app/store';
import { initTheme } from '@/shared/hooks/useTheme';
import { setTokens, logout } from '@/features/authentication';
import { apiClient } from '@/shared/services/api/client';
import { setupInterceptors } from '@/shared/services/api/interceptors';

/*
  Interceptors are wired here rather than inside client.ts: they need the store,
  the store runs rootSaga at module scope, and the sagas reach the client
  through the services. Importing them from client.ts would close that cycle.
*/
setupInterceptors(apiClient, {
  getToken: () => store.getState().auth.token,
  getTokenType: () => store.getState().auth.tokenType,
  /* The entity picker's choice, which the platform scopes every hierarchy read by. */
  getSelectedEntityId: () => store.getState().selectedEntity.id,
  onTokensRefreshed: (tokens) => {
    store.dispatch(setTokens(tokens));
  },
  /*
    `logout()`, not `logoutStart()`: the session is already dead, so calling
    POST /auth/logout with a rejected token is pointless. This clears state and
    lets ProtectedRoute redirect to /login (loginapi.md step 6).
  */
  onSessionExpired: () => {
    store.dispatch(logout());
  },
});

/*
  Before the first render, not in an effect: the theme is an attribute on
  <html> that the CSS keys off, so applying it after mount would paint the
  default palette first and then swap.
*/
initTheme();

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
